import React, {Fragment, useEffect, useRef, useState} from "react";
import ReactDOM from "react-dom";
import {Col, Container, Form, FormGroup, Row} from "reactstrap";
import {useDispatch, useSelector} from "react-redux";
import Switch from "react-switch";
import dragula from "react-dragula";
import Breadcrumb from "../../../../layout/breadcrumb";
import PreLoader from "../../../../share-components/ui/preLoader";
import CommonDataTable from "../../../../share-components/table/commonDataTable";
import withReducer from "../../../../store/withReducer";
import productReducer from "../../store";
import {
    getAllTrendingColours,
    setTrendingColourOrder,
    sortTrendingColours,
    toggleTrendingColour
} from "../../store/trendingColourSlice";
import "./trendingColour.css";


// Must match .colour-preview in trendingColour.css — used to flip the box above the
// swatch near the viewport edge.
const PREVIEW_WIDTH = 128;
const PREVIEW_HEIGHT = 152;

const dataMenu = [
    {
        type: 0,
        text: 'Products',
        link: '/product'
    },
    {
        type: 1,
        text: 'Product Setting',
        link: ''
    },
    {
        type: 1,
        text: 'Trending Colors',
        link: ''
    }
];

const TrendingColour = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [name, setName] = useState('');
    const [preview, setPreview] = useState(null);
    const trendingColourState = useSelector(({product}) => product.trendingColour);
    const currentUser = useSelector(({authReducer}) => authReducer.data.user);
    const canUpdate = (currentUser.permission).includes('Update Colour');

    const colours = trendingColourState.data;

    const tableWrapperRef = useRef(null);
    const drakeRef = useRef(null);
    const onDropRef = useRef(null);

    useEffect(() => {
        dispatch(getAllTrendingColours()).then(res => {
            if (!res.error) setLoading(false);
        });
    }, [dispatch]);

    // Search is client side — the whole house-colour list is already loaded.
    const gridData = colours.filter(item =>
        (item.name ? item.name : '').toLowerCase().includes(name.toLowerCase())
    );

    // Dragging only ever reorders the rows on screen, which is a subset of the list while a
    // search is active. Drop the reordered ids back into the slots those same colours held
    // in the full list, so the saved order is always the whole list.
    const buildFullOrder = (visibleIds) => {
        const fullIds = colours.map(item => String(item.id));
        const visible = {};
        visibleIds.forEach(id => {
            visible[id] = true;
        });

        const slots = [];
        fullIds.forEach((id, index) => {
            if (visible[id]) slots.push(index);
        });

        const merged = fullIds.slice();
        slots.forEach((slot, i) => {
            merged[slot] = visibleIds[i];
        });

        return merged;
    };

    // Only trending colours hold a slot, so a reorder posts just those ids. Everything else
    // stays at sort_order 0 and keeps falling back to alphabetical, which is what keeps a
    // 600-row grid readable.
    const trendingIdsInOrder = (fullOrder) => {
        const isTrending = {};
        colours.forEach(item => {
            if (item.is_trending) {
                isTrending[String(item.id)] = true;
            }
        });
        return fullOrder.filter(id => isTrending[id]);
    };

    // The typed number IS the slot — it is saved as-is and nothing else is renumbered, so
    // numbers can be typed down a run of rows without each entry disturbing the last.
    // Duplicates are allowed; the grid orders by sort_order and ties fall back to name.
    const commitOrder = (row, raw, input) => {
        const target = parseInt(raw, 10);

        if (isNaN(target) || target < 1 || target === row.sort_order) {
            if (input) {
                input.value = row.sort_order;
            }
            return;
        }

        dispatch(setTrendingColourOrder({id: row.id, sort_order: target}));
    };

    onDropRef.current = (source) => {
        const visibleIds = [];
        // Deliberately NOT data-row-id: react-data-table-component already puts that on
        // every row div, set to the keyField (the name), which would double up the matches.
        source.querySelectorAll('[data-colour-id]').forEach(item => {
            visibleIds.push(item.getAttribute('data-colour-id'));
        });

        dispatch(sortTrendingColours({sortData: trendingIdsInOrder(buildFullOrder(visibleIds))}));
    };

    // react-data-table-component renders its rows into .rdt_TableBody, so that is the
    // dragula container. gridData.length is a dependency because the table drops that node
    // entirely when a search matches nothing, and builds a fresh one when rows come back.
    useEffect(() => {
        if (loading || !canUpdate || !tableWrapperRef.current) {
            return undefined;
        }

        const container = tableWrapperRef.current.querySelector('.rdt_TableBody');
        if (!container) {
            return undefined;
        }

        const drake = dragula([container], {
            moves: function (el, source, handle) {
                return handle.classList.contains('drag-handle') || (handle.closest && handle.closest('.drag-handle'));
            },
        });

        drake.on('drop', (el, target, source) => {
            // Read the new order off the DOM BEFORE cancelling the drag, otherwise
            // cancel(true) restores the old order and we would persist that instead.
            onDropRef.current(source);

            // Hand the DOM back to React; the thunk re-fetches the sorted list.
            drake.cancel(true);
        });

        drakeRef.current = drake;

        return () => {
            drake.destroy();
            drakeRef.current = null;
        };
    }, [loading, canUpdate, colours, gridData.length]);

    const onToggleTrending = (row, checked) => {
        dispatch(toggleTrendingColour({id: row.id, is_trending: checked}));
    };

    // color_code is free text, so only paint the swatch when the stored value really is a
    // hex colour. Anything else still shows its text, just without a misleading preview.
    const swatchColour = (code) => {
        if (!code) {
            return null;
        }
        const value = String(code).trim();
        if (!/^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(value)) {
            return null;
        }
        return value.charAt(0) === '#' ? value : `#${value}`;
    };

    // The grid scrolls inside an overflow container, so an in-cell tooltip would be clipped
    // at the row edge. The preview is portalled to <body> and positioned off the swatch.
    const showPreview = (event, colour, code) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const fitsBelow = rect.bottom + PREVIEW_HEIGHT + 8 <= window.innerHeight;

        setPreview({
            colour,
            code,
            top: fitsBelow ? rect.bottom + 8 : rect.top - PREVIEW_HEIGHT - 8,
            left: Math.min(rect.left, window.innerWidth - PREVIEW_WIDTH - 8),
        });
    };

    const tableColumns = [
        {
            name: '',
            selector: row => <span data-colour-id={row.id}>
                {canUpdate && row.is_trending ? <span className="drag-handle" title="Drag to reorder">
                    <i className="fa fa-ellipsis-v"></i>
                    <i className="fa fa-ellipsis-v"></i>
                </span> : ''}
            </span>,
            sortable: false,
            center: true,
            width: '60px',
        },
        {
            name: 'Name',
            selector: row => row.name,
            // Column sorting is off across the grid: the row order IS the saved sort order,
            // so a re-sorted grid would persist the wrong order on the next drag.
            sortable: false,
            left: true,
        },
        {
            name: 'Colour Code',
            selector: row => {
                const swatch = swatchColour(row.color_code);
                return row.color_code ? <span className="colour-code-cell">
                    {swatch ? <span
                        className="colour-swatch"
                        style={{backgroundColor: swatch}}
                        onMouseEnter={(e) => {
                            showPreview(e, swatch, row.color_code);
                        }}
                        onMouseLeave={() => {
                            setPreview(null);
                        }}
                    ></span> : ''}
                    <span>{row.color_code}</span>
                </span> : '-';
            },
            sortable: false,
            left: true,
        },
        {
            name: 'Trending',
            selector: row => <Switch
                checked={!!row.is_trending}
                onChange={(checked) => {
                    onToggleTrending(row, checked);
                }}
                disabled={!canUpdate}
                className="react-switch"
                uncheckedIcon={false}
                checkedIcon={false}
                height={20}
                width={40}
            />,
            sortable: false,
            center: true,
        },
        {
            name: 'Sort Order',
            // Uncontrolled on purpose: a controlled input would re-render every row in the
            // grid on each keystroke. The key carries sort_order so the field refreshes
            // when a save changes the slot, but never while it is being typed in.
            selector: row => row.is_trending ? <input
                key={`${row.id}-${row.sort_order}`}
                type="number"
                min="1"
                className="form-control sort-order-input"
                defaultValue={row.sort_order}
                disabled={!canUpdate}
                onBlur={(e) => {
                    commitOrder(row, e.target.value, e.target);
                }}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.target.blur();
                    }
                }}
            /> : '-',
            sortable: false,
            center: true,
        },
    ];

    return (
        <Fragment>
            <Container fluid={true} className="product-wrapper">
                {loading ? <><PreLoader></PreLoader></> : <>
                    <Breadcrumb data={dataMenu} title="Trending Colors"/>
                    <div className="product-grid">
                        <div className="feature-products">
                            <Row>
                                <Col xl="12" sm="12">
                                    <Form>
                                        <FormGroup>
                                            <input onChange={(e) => setName(e.target.value)} name="name"
                                                   type="text" className="form-control"
                                                   placeholder="Search By Name"/>
                                        </FormGroup>
                                    </Form>
                                </Col>
                            </Row>

                            <div className="table-responsive product-table mt-4 trending-colour-table"
                                 ref={tableWrapperRef}>
                                <CommonDataTable
                                    headerColumns={tableColumns}
                                    gridData={gridData}
                                    keyField="name"
                                    pagination={false}
                                    noAction={true}
                                />
                            </div>

                        </div>
                    </div>
                </>
                }
            </Container>
            {preview ? ReactDOM.createPortal(
                <div className="colour-preview" style={{top: preview.top, left: preview.left}}>
                    <div className="colour-preview-swatch" style={{backgroundColor: preview.colour}}></div>
                    <div className="colour-preview-code">{preview.code}</div>
                </div>,
                document.body
            ) : ''}
        </Fragment>
    );
};

export default withReducer([{product: productReducer}])(TrendingColour)
